import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #walletResizeObserver;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('click', this.#handleCartLink, true);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.#openFromUrl();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#walletResizeObserver?.disconnect();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('click', this.#handleCartLink, true);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Keep cart links on the current storefront page and open the drawer instead.
   * Modified clicks are left alone; a direct /cart request is handled by theme.liquid.
   *
   * @param {MouseEvent} event
   */
  #handleCartLink = (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const target = event.target;
    const link = target instanceof Element ? target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement) || link.target === '_blank' || link.hasAttribute('download')) return;

    const url = new URL(link.href, window.location.href);
    const rootPath = window.Shopify?.routes?.root || '/';
    const cartPath = `${rootPath.replace(/\/$/, '')}/cart`;

    if (url.origin !== window.location.origin || url.pathname.replace(/\/$/, '') !== cartPath) return;

    event.preventDefault();
    this.open();
  };

  /** Open the drawer after a direct /cart visit has been redirected to the storefront. */
  #openFromUrl() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('open_cart_drawer') !== 'true') return;

    url.searchParams.delete('open_cart_drawer');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    requestAnimationFrame(() => this.open());
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');
    const items = dialog.querySelector('.cart-drawer__items');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    // The items area is the only scroll container, so the summary remains pinned
    // outside it. Keeping this state deterministic avoids iOS Safari switching the
    // footer to static positioning after measuring its dynamic viewport height.
    dialog.setAttribute('cart-summary-sticky', 'true');
    if (items instanceof HTMLElement) items.scrollTop = 0;
    this.#fitWalletRow(dialog);
  }

  /** @param {HTMLDialogElement} dialog */
  #fitWalletRow(dialog) {
    this.#walletResizeObserver?.disconnect();

    const updateScale = () => {
      const walletContainer = dialog.querySelector('.additional-checkout-buttons');
      if (!(walletContainer instanceof HTMLElement) || walletContainer.clientWidth === 0) return;

      const scale = Math.min(1, walletContainer.clientWidth / 640);
      dialog.style.setProperty('--cart-wallet-scale', scale.toFixed(4));
      dialog.style.setProperty('--cart-wallet-height', `${(55 * scale).toFixed(2)}px`);
      dialog.style.setProperty('--cart-wallet-radius', `${(10 / scale).toFixed(2)}px`);
    };

    this.#walletResizeObserver = new ResizeObserver(updateScale);
    this.#walletResizeObserver.observe(dialog);
    requestAnimationFrame(updateScale);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
